import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { CrmDisplayConfigField } from '@plato/data/model/crm-config';
import {
    getCrmFieldNumberFormat,
    isNumberFormatStyle,
    isPresetFieldPath,
    presetFields,
} from '@plato/data/model/crm-config';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { CopyButton } from '@plato/ui-legacy/components/ui/buttons/copy-button';
import { FormatValue } from '@plato/ui-legacy/components/ui/format-value';
import type { FormatValueStringFormat } from '@plato/ui-legacy/components/ui/format-value-string';
import { formatValueString } from '@plato/ui-legacy/components/ui/format-value-string';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectValue,
} from '@plato/ui/components/select';
import { cn } from '@plato/ui/util/classes';
import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFullAddressString } from '../../../lib/address-helper.ts';
import { CustomerDetailCreditLimit } from '../../customer-detail/components/customer-detail-credit-limit.tsx';
import {
    useCrmContext,
    useCrmFieldConfig,
    useCrmFieldRawValue,
    useCrmFieldStringValue,
} from './crm-context.tsx';
import { CrmInput, CrmTextarea, inputStyle } from './crm-input.tsx';
import type { ChangeAction, DraftInputProps } from './draft-input.tsx';
import { DraftInput } from './draft-input.tsx';
import { OverlayInput } from './overlay-input.tsx';

type CrmFieldOption = { value: string; label: string };

/** Read mode hides the field's border until the row is hovered. */
const ghostTriggerClass = 'border-transparent hover:border-line';

export type CrmFieldMode = 'auto' | 'edit';
export type CrmFieldCopy = 'always' | 'never';

export type CrmFieldValueProps = {
    className?: string;
    config: CrmDisplayConfigField;
    id?: string;
    mode?: CrmFieldMode;
    showCopy?: CrmFieldCopy;
};

export function CrmFieldValue(props: CrmFieldValueProps) {
    const { config, id, showCopy } = props;
    const { entityType } = useCrmContext();
    const displayText = useCrmFieldDisplayText(config);

    if (isPresetFieldPath(config.path)) {
        if (entityType === 'address') {
            return <AddressPresetField {...props} />;
        }
        if (entityType === 'customer') {
            return <CustomerPresetField {...props} />;
        }
        return;
    }

    if (!config.writable) {
        return (
            <ReadOnlyField
                id={id}
                showCopy={showCopy}
                textValue={displayText}
                className={config.format === 'multiline' ? 'whitespace-pre-wrap' : undefined}
            >
                <CrmFieldValueFormat config={config} />
            </ReadOnlyField>
        );
    }

    if (config.options) {
        return <CrmFieldSelect {...props} />;
    }
    if (config.format === 'birthday') {
        return <CrmFieldBirthdayInput {...props} />;
    }
    if (config.format === 'yes-no') {
        return <CrmFieldSelectBoolean {...props} />;
    }
    if (config.format === 'multiline') {
        return <CrmFieldMultilineInput {...props} />;
    }
    return <CrmFieldValueWritable {...props} />;
}

function AddressPresetField({ config, ...fieldProps }: CrmFieldValueProps) {
    const { data } = useCrmContext('address');

    let textValue: string | undefined;
    let children: ReactNode | undefined;

    if (config.path === presetFields.address.formatted) {
        textValue = data && getFullAddressString(data, undefined, '\n');
    }

    return (
        <ReadOnlyField {...fieldProps} textValue={textValue} className="whitespace-pre">
            {children}
        </ReadOnlyField>
    );
}

function CustomerPresetField({ config, ...fieldProps }: CrmFieldValueProps) {
    const { data } = useCrmContext('customer');
    const creditLimitUsed = useCrmFieldConfig({ path: '/creditLimitUsed' });
    const creditLimitAllowed = useCrmFieldConfig({ path: '/creditLimitAllowed' });
    const creditLimitUsedText = useCrmFieldDisplayText(creditLimitUsed);
    const creditLimitAllowedText = useCrmFieldDisplayText(creditLimitAllowed);

    let textValue: string | undefined;
    let children: ReactNode | undefined;

    if (config.path === presetFields.customer.address) {
        textValue = data?.address && getFullAddressString(data.address, data.customerName);
    } else if (config.path === presetFields.customer.creditLimit) {
        // only offer a copy when at least one side has a number, never a bare " / "
        textValue =
            creditLimitUsedText || creditLimitAllowedText
                ? `${creditLimitUsedText} / ${creditLimitAllowedText}`
                : undefined;
        children = (
            <>
                <CrmFieldValueFormat config={creditLimitUsed} />
                {' / '}
                <CrmFieldValueFormat config={creditLimitAllowed} />
            </>
        );
    } else if (config.path === presetFields.customer.internalSalesReps) {
        textValue = data?.salesReps
            .filter((x) => x.isInternal === true)
            .map(({ salesRepId, salesRepName }) => `${salesRepId}: ${salesRepName}`)
            .join(', ');
    } else if (config.path === presetFields.customer.externalSalesReps) {
        textValue = data?.salesReps
            .filter((x) => x.isInternal === false)
            .map(({ salesRepId, salesRepName }) => `${salesRepId}: ${salesRepName}`)
            .join(', ');
    } else if (config.path === presetFields.customer.gridCreditLimit) {
        if (data) {
            children = <CustomerDetailCreditLimit customerId={data.customerId} compact />;
        }
    }

    return (
        <ReadOnlyField {...fieldProps} textValue={textValue}>
            {children}
        </ReadOnlyField>
    );
}

type ReadOnlyFieldProps = {
    className?: string;
    children?: ReactNode;
    id?: string;
    showCopy?: CrmFieldCopy;
    textValue?: string;
};

function ReadOnlyField({ className, children, id, showCopy = 'never', textValue }: ReadOnlyFieldProps) {
    const readonlyStyle = cn(inputStyle({ interactive: false, variant: 'readonly' }), 'min-h-10 break-words');

    return showCopy === 'never' ? (
        <div id={id} className={cn(readonlyStyle, className)}>
            {children ?? textValue}
        </div>
    ) : (
        <div className={cn(className, 'flex items-center')}>
            <div id={id} className={cn(readonlyStyle, 'grow')}>
                {children ?? textValue}
            </div>
            <FieldCopyButton value={textValue} />
        </div>
    );
}

export function CrmFieldValueWritable({ config, id, mode = 'auto', showCopy = 'never' }: CrmFieldValueProps) {
    const [value] = useCrmFieldRawValue(config.path);
    const [, setValueString] = useCrmFieldStringValue(config.path);
    const displayText = useCrmFieldDisplayText(config);
    const inputVariant = mode === 'edit' ? 'border' : 'default';

    // we don't use useCrmFieldStringValue here because we don't want its formatting

    /* eslint-disable-next-line @typescript-eslint/no-base-to-string 
    -- this shouldn't be anything other than a built-in */
    const textValue = value ? String(value) : '';

    const overlayStyle = inputStyle({
        hover: config.writable ? 'always' : 'never',
        interactive: false,
        variant: config.writable ? inputVariant : 'readonly',
    });

    function handleValueChanged(value: DraftInputProps['value'], action: ChangeAction) {
        if (action !== 'escape') {
            setValueString(String(value));
        }
    }

    return (
        <div className="flex items-center">
            <OverlayInput
                containerClassName="grow"
                value={textValue}
                overlay={
                    <div className={cn('h-full cursor-pointer break-words', overlayStyle)}>
                        <CrmFieldValueFormat config={config} />
                    </div>
                }
                hideOverlay={mode === 'edit'}
                disabled={!config.writable}
            >
                <DraftInput id={id} onValueChanged={handleValueChanged} selectOnFocus>
                    <CrmInput variant={inputVariant} placeholder={config.placeholder} />
                </DraftInput>
            </OverlayInput>
            {showCopy !== 'never' && <FieldCopyButton value={displayText} />}
        </div>
    );
}

export function CrmFieldBirthdayInput({ config, id, mode, showCopy = 'never' }: CrmFieldValueProps) {
    // a birthday input has an optional year
    const { i18n, t } = useTranslation('customer-detail');
    const [value, setValue] = useCrmFieldStringValue(config.path);
    const displayText = useCrmFieldDisplayText(config);
    const triggerClassName = cn('h-10 w-auto', mode !== 'edit' && ghostTriggerClass);

    const thisYear = new Date().getFullYear();

    const years: CrmFieldOption[] = [
        { value: '0000', label: t('field_option__year_unknown') },
        ...Array.from({ length: 130 }, (_, i) => {
            const year = `${thisYear - i}`;
            return { value: year, label: year };
        }),
    ];

    const months = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(i18n.language, { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => ({
            label: formatter.format(new Date(2020, i, 10)),
            value: `${i + 1}`.padStart(2, '0'),
        }));
    }, [i18n.language]);

    const days: CrmFieldOption[] = Array.from({ length: 31 }, (_, i) => ({
        value: `${i + 1}`.padStart(2, '0'),
        label: (i + 1).toString(),
    }));

    const [year, setYear] = useState('');
    const [month, setMonth] = useState('');
    const [day, setDay] = useState('');

    useEffect(() => {
        const [, year, month, day] = /^\s*(\d\d\d\d)-(\d\d)-(\d\d)\s*$/.exec(value) ?? [];
        // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
        setYear(year ?? '');
        setMonth(month ?? '');
        setDay(day ?? '');
    }, [value]);

    useEffect(() => {
        if (!month || !day) {
            return;
        }
        const date = new Date(`${year || '0000'}-${month}-${day}`);
        if (Number.isNaN(date.getTime())) {
            return;
        }
        const value = date.toISOString().slice(0, 10);
        setValue(value);
    }, [year, month, day, setValue]);

    return (
        <div className="flex items-center gap-2">
            <Select
                title={t('field_placeholder__year_optional')}
                items={years}
                value={year}
                onValueChange={(option) => {
                    setYear(option ?? '');
                }}
            >
                <SelectTrigger id={id} className={triggerClassName}>
                    <SelectValue placeholder={t('field_placeholder__year_optional')} />
                </SelectTrigger>
                <SelectContent>
                    <SelectList>
                        {(option: CrmFieldOption) => <SelectItem key={option.value} value={option} />}
                    </SelectList>
                </SelectContent>
            </Select>
            <Select
                title={t('field_placeholder__month')}
                items={months}
                value={month}
                onValueChange={(option) => {
                    setMonth(option ?? '');
                }}
            >
                <SelectTrigger id={id} className={triggerClassName}>
                    <SelectValue placeholder={t('field_placeholder__month')} />
                </SelectTrigger>
                <SelectContent>
                    <SelectList>
                        {(option: CrmFieldOption) => <SelectItem key={option.value} value={option} />}
                    </SelectList>
                </SelectContent>
            </Select>
            <Select
                title={t('field_placeholder__day')}
                items={days}
                value={day}
                onValueChange={(option) => {
                    setDay(option ?? '');
                }}
            >
                <SelectTrigger id={id} className={triggerClassName}>
                    <SelectValue placeholder={t('field_placeholder__day')} />
                </SelectTrigger>
                <SelectContent>
                    <SelectList>
                        {(option: CrmFieldOption) => <SelectItem key={option.value} value={option} />}
                    </SelectList>
                </SelectContent>
            </Select>
            {value && (
                <Button
                    variant="icon"
                    icon={X}
                    className="h-10 w-10"
                    onClick={() => {
                        setValue('');
                    }}
                />
            )}
            {showCopy !== 'never' && <FieldCopyButton value={displayText} />}
        </div>
    );
}

export function CrmFieldMultilineInput({
    config,
    id,
    mode = 'auto',
    showCopy = 'never',
}: CrmFieldValueProps) {
    const [value] = useCrmFieldRawValue(config.path);
    const [, setValueString] = useCrmFieldStringValue(config.path);
    const displayText = useCrmFieldDisplayText(config);
    const inputVariant = mode === 'edit' ? 'border' : 'default';

    const textValue = typeof value === 'string' ? value : '';

    const overlayStyle = inputStyle({
        hover: config.writable ? 'always' : 'never',
        interactive: false,
        variant: config.writable ? inputVariant : 'readonly',
    });

    function handleValueChanged(value: DraftInputProps['value'], action: ChangeAction) {
        if (action !== 'escape') {
            setValueString(String(value));
        }
    }

    return (
        <div className="flex items-start">
            <OverlayInput
                containerClassName="grow"
                value={textValue}
                overlay={
                    <div
                        className={cn('h-full cursor-pointer break-words whitespace-pre-wrap', overlayStyle)}
                    >
                        <CrmFieldValueFormat config={config} />
                    </div>
                }
                hideOverlay={mode === 'edit'}
                disabled={!config.writable}
            >
                <DraftInput multiline id={id} onValueChanged={handleValueChanged}>
                    <CrmTextarea variant={inputVariant} placeholder={config.placeholder} />
                </DraftInput>
            </OverlayInput>
            {showCopy !== 'never' && <FieldCopyButton value={displayText} />}
        </div>
    );
}

export function CrmFieldSelect({ config, id, mode = 'auto', showCopy = 'never' }: CrmFieldValueProps) {
    const [value] = useCrmFieldRawValue(config.path);
    const [, setValueString] = useCrmFieldStringValue(config.path);
    const textValue = typeof value === 'string' ? value : undefined;
    const options = config.options ?? [];

    return (
        <div className="flex items-center">
            <Select
                title={config.label ?? ''}
                items={options}
                value={textValue}
                onValueChange={(option) => {
                    setValueString(option ?? '');
                }}
            >
                <SelectTrigger
                    id={id}
                    // the trigger is shrink-0 by default; let it give way so a long
                    // option label can't push the copy button out of its column
                    className={cn('h-10 w-auto grow shrink min-w-0', mode !== 'edit' && ghostTriggerClass)}
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectList>{(option: string) => <SelectItem key={option} value={option} />}</SelectList>
                </SelectContent>
            </Select>
            {showCopy !== 'never' && <FieldCopyButton value={textValue} />}
        </div>
    );
}

export function CrmFieldSelectBoolean({ config, id, mode = 'auto', showCopy = 'never' }: CrmFieldValueProps) {
    const { t } = useTranslation();
    const [value, setValue] = useCrmFieldRawValue(config.path);
    const textValue = typeof value === 'boolean' ? `${value}` : 'undefined';

    const options: CrmFieldOption[] = [
        { value: 'undefined', label: t('not_set') },
        { value: 'true', label: t('yes_no__true') },
        { value: 'false', label: t('yes_no__false') },
    ];

    // copy what the user sees, not the raw boolean; "not set" has nothing to copy
    const copyValue = typeof value === 'boolean' ? t(value ? 'yes_no__true' : 'yes_no__false') : undefined;

    return (
        <div className="flex items-center">
            <Select
                title={config.label ?? ''}
                items={options}
                value={textValue}
                onValueChange={(option) => {
                    if (option === 'true') {
                        setValue(true);
                    } else if (option === 'false') {
                        setValue(false);
                    } else {
                        setValue(undefined);
                    }
                }}
            >
                <SelectTrigger
                    id={id}
                    // the trigger is shrink-0 by default; let it give way so a long
                    // option label can't push the copy button out of its column
                    className={cn('h-10 w-auto grow shrink min-w-0', mode !== 'edit' && ghostTriggerClass)}
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectList>
                        {(option: CrmFieldOption) => <SelectItem key={option.value} value={option} />}
                    </SelectList>
                </SelectContent>
            </Select>
            {showCopy !== 'never' && <FieldCopyButton value={copyValue} />}
        </div>
    );
}

type CopyButtonProps = {
    value?: unknown;
};

/**
 * The copy affordance every CRM field gets: always visible, always the same
 * 40px box next to the value, disabled when there is nothing to copy.
 */
function FieldCopyButton({ value }: CopyButtonProps) {
    const { t } = useTranslation();

    return (
        <CopyButton
            // my-0 cancels the inline glyph's negative margin so it centres in the row
            className="size-10 shrink-0 my-0 ml-cozy"
            textToCopy={toCopyText(value)}
            label={t('copy_tooltip', { ns: 'common' })}
        />
    );
}

/** Renders any CRM value as the plain text we put on the clipboard. */
export function toCopyText(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return `${value}`;
    }
    if (value instanceof Date) {
        return value.toISOString().slice(0, 10);
    }
    return '';
}

/**
 * Formats a field value according to the field config.
 */
function CrmFieldValueFormat({ config }: CrmFieldValueProps): ReactElement {
    const [fieldValue] = useCrmFieldRawValue(config.path);
    return <FormatValueWithDisplayConfig config={config} value={fieldValue} />;
}

/**
 * What `CrmFieldValueFormat` puts on screen for this field, as a string. Copy
 * buttons use it so the clipboard holds "6.927 €" rather than 6926.83000001.
 */
function useCrmFieldDisplayText(config: CrmDisplayConfigField): string {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const { defaultCurrency } = useAppConfig();
    const [value] = useCrmFieldRawValue(config.path);

    return formatValueString({
        locale,
        numberFormatOptions: getCrmFieldNumberFormat(config, { currency: defaultCurrency }),
        stringFormat: getCrmFieldStringFormat(config),
        t,
        value,
    });
}

export type FormatValueWithDisplayConfigProps = CrmFieldValueProps & {
    fallback?: ReactNode;
    value?: unknown;
};

export function FormatValueWithDisplayConfig({
    config,
    fallback = '-',
    value,
}: FormatValueWithDisplayConfigProps): ReactElement {
    const { defaultCurrency } = useAppConfig();

    return (
        <FormatValue
            fallback={fallback}
            numberFormatOptions={getCrmFieldNumberFormat(config, { currency: defaultCurrency })}
            stringFormat={getCrmFieldStringFormat(config)}
            value={value}
        />
    );
}

/** The `FormatValue` string format for a field, if its format isn't a number style. */
function getCrmFieldStringFormat(config: CrmDisplayConfigField): FormatValueStringFormat | undefined {
    if (
        !config.format ||
        isNumberFormatStyle(config.format) ||
        config.format === 'number' ||
        config.format === 'yes-no' ||
        config.format === 'multiline'
    ) {
        return undefined;
    }
    return config.format;
}

export function getFieldKey(field: CrmDisplayConfigField, index?: number) {
    // simplest way to get a unique value for the field config
    return JSON.stringify({ field, index });
}
